import {Separator} from "@/components/ui/separator";
import {SidebarTrigger} from "@/components/ui/sidebar";
import {useLocation} from "react-router-dom";
import {Filter, Calendar, Trash, Clock} from 'lucide-react';
import {useTranslation} from "react-i18next";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {Button} from "@/components/ui/button";
import React from "react";

const AVAILABLE_BINS = ['all', 'R1', 'R2', 'R3', 'R4', 'R5', 'R6', 'R7', 'R9', 'R13', 'R14', 'R15', 'R17', 'IST01', 'IST03', 'B1', 'B3', 'B2'];
const WASTE_TYPES = ['pmd', 'organic', 'paper', 'rest'];

// Updated TIME_PERIODS to match format from Layout.jsx
const TIME_PERIODS = ['Day', 'Week', 'Month', '3 Months', '6 Months', 'Year'];
const AVAILABLE_YEARS = ['2020', '2021', '2022', '2023', '2024', '2025'];

// Updated WASTE_DISPLAY_NAMES to match format expected
const WASTE_DISPLAY_NAMES = {
    pmd: 'PMD',
    organic: 'Organic',
    paper: 'Paper',
    rest: 'Residual'
};

export function CostSavingsHeader({
                                      selectedBins,
                                      setSelectedBins,
                                      selectedWaste,
                                      setSelectedWaste,
                                      selectedPeriod,
                                      setSelectedPeriod,
                                      selectedYear,
                                      setSelectedYear,
                                      timeRangeOptions,
                                      toggleBinSelection,
                                      allBins,
                                      binDropdownOpen,
                                      setBinDropdownOpen,
                                      timeRangeDropdownOpen,
                                      setTimeRangeDropdownOpen,
                                  }) {
    const location = useLocation();
    const {t} = useTranslation();

    const getHeaderTitle = () => {
        return t("costSavingstitle");
    };

    // Helper function for time range translation with direct approach
    const getTimeRangeTranslation = (timeRange) => {
        const translations = {
            'day': t('timeRanges.Day'),
            'week': t('timeRanges.Week'),
            'month': t('timeRanges.Month'),
            '3months': t('timeRanges.3Month'),
            '6months': t('timeRanges.6Month'),
            'year': t('timeRanges.Year'),
            // Also handle the display format versions
            'Day': t('timeRanges.Day'),
            'Week': t('timeRanges.Week'),
            'Month': t('timeRanges.Month'),
            '3 Months': t('timeRanges.3Month'),
            '6 Months': t('timeRanges.6Month'),
            'Year': t('timeRanges.Year')
        };

        return translations[timeRange] || timeRange;
    };

    const getWasteTypeTranslation = (wasteType) => {
        const translations = {
            'organic': t('wasteImpact.streams.organic'),
            'pmd': t('wasteImpact.streams.pmd'),
            'paper': t('wasteImpact.streams.paper'),
            'residual': t('wasteImpact.streams.residual'),
        };

        return translations[wasteType] || wasteType;
    };

    // Create a mapping for time period display that uses translations
    const getTimePeriodDisplay = (period) => {
        return getTimeRangeTranslation(period);
    };


    // Use toggleBinSelection from Layout if available, otherwise use local function
    const toggleBin = (bin) => {
        if (toggleBinSelection) {
            toggleBinSelection(bin);
        } else if (setSelectedBins) {
            setSelectedBins(prev =>
                prev.includes(bin)
                    ? prev.filter(b => b !== bin)
                    : [...prev, bin]
            );
        }
    };

    const selectAllBins = () => {
        if (toggleBinSelection && allBins?.length > 0) {
            // First clear all selections
            clearAllBins();
            // Then select all bins one by one using the toggle function
            allBins.forEach(bin => toggleBinSelection(bin));
        } else if (setSelectedBins) {
            // Use direct state setting if available
            if (allBins && allBins.length > 0) {
                setSelectedBins(allBins);
            } else {
                setSelectedBins(AVAILABLE_BINS);
            }
        }
    };

    const clearAllBins = () => {
        if (toggleBinSelection && selectedBins?.length > 0) {
            // Deselect each bin one by one using toggle
            [...selectedBins].forEach(bin => toggleBinSelection(bin));
        } else if (setSelectedBins) {
            // Use direct state setting if available
            setSelectedBins([]);
        }
    };

    // Use timeRangeOptions from props if available, otherwise use local constant
    const displayTimePeriods = timeRangeOptions || TIME_PERIODS;

    // Handle bin checkbox change without closing dropdown
    const handleBinChange = (bin) => {
        toggleBin(bin);
        // No return false here - it won't work
    };

    // Handle time period change without closing dropdown
    const handlePeriodChange = (period) => {
        setSelectedPeriod(period);
        // No return false here - it won't work
    };

    // This is needed to override the default behavior of checkbox items closing the dropdown
    const handleOpenChange = (open, menuType) => {
        if (menuType === 'bin') {
            setBinDropdownOpen(open);
        } else if (menuType === 'period') {
            setTimeRangeDropdownOpen(open);
        }
    };

    // Translate waste display names
    const getLocalizedWasteName = (wasteType) => {
        switch (wasteType) {
            case 'pmd':
                return t("pmdlabel");
            case 'organic':
                return t("organiclabel");
            case 'paper':
                return t("paperlabel");
            case 'rest':
                return t("residuallabel");
            default:
                return wasteType;
        }
    };

    return (
        <header
            className="group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 flex h-12 shrink-0 items-center gap-2 transition-[width,height] ease-linear sticky top-0 backdrop-blur-md z-30 border-b w-full max-w-[100vw] overflow-x-hidden px-2 sm:px-4">
            <div className="flex w-full items-center justify-between gap-1">
                <div className="flex items-center min-w-0 max-w-[60%] sm:max-w-none">
                    <SidebarTrigger className="-ml-1 h-8 w-8 sm:h-9 sm:w-9"/>
                    <Separator
                        orientation="vertical"
                        className="mx-1 sm:mx-2 data-[orientation=vertical]:h-4"
                    />
                    <h1 className="text-sm sm:text-base font-medium truncate">{getHeaderTitle()}</h1>
                </div>

                <div className="flex gap-1 sm:gap-2">
                    {/* Year Filter - Grayed out and disabled */}
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm text-muted-foreground cursor-not-allowed"
                        disabled
                    >
                        <Calendar className="h-3 w-3 sm:h-4 sm:w-4 opacity-50 mr-1"/>
                        <span className="hidden sm:inline">{t("header.filters.year")}: </span>
                        {selectedYear || '2025'}
                    </Button>

                    {/* Waste Type Filter */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm"
                                disabled={true}
                            >
                                <Filter className="h-3 w-3 sm:h-4 sm:w-4 opacity-50 mr-1"/>
                                <span className="hidden sm:inline">{t("header.filters.wasteTypes")}: </span>
                                {getWasteTypeTranslation(selectedWaste) || t("common.select")}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-40">
                            <DropdownMenuLabel>{t("header.filters.selectWasteType")}</DropdownMenuLabel>
                            <DropdownMenuSeparator/>
                            {WASTE_TYPES.map(waste => (
                                <DropdownMenuCheckboxItem
                                    key={waste}
                                    checked={selectedWaste === waste}
                                    onCheckedChange={() => setSelectedWaste(waste)}
                                >
                                    {getLocalizedWasteName(waste)}
                                </DropdownMenuCheckboxItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>


                    {/* Bin Filter - Using controlled open state */}
                    <DropdownMenu open={binDropdownOpen} onOpenChange={(open) => handleOpenChange(open, 'bin')}>
                        <DropdownMenuTrigger asChild>
                            { /* This is the bin button */}
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm"
                            >
                                <Trash className="h-3 w-3 sm:h-4 sm:w-4 opacity-50 mr-1"/>
                                <span className="hidden sm:inline">{t('header.filters.bins')}:  </span>
                                {selectedBins?.length === (allBins?.length || AVAILABLE_BINS.length)
                                    ? t("common.all")
                                    : selectedBins?.length === 0
                                        ? t("common.clear")
                                        : selectedBins?.length === 1
                                            ? selectedBins[0]
                                            : `${selectedBins?.length}`}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            className="w-56"
                            onCloseAutoFocus={(e) => {
                                // Prevent auto-focus on close, which can cause unwanted scrolling
                                e.preventDefault();
                            }}
                        >
                            <DropdownMenuLabel className="flex justify-between items-center">
                                <span>{t('header.filters.selected')}</span>
                                <div className="flex gap-1">
                                    <Button
                                        variant="ghost"
                                        size="xs"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            selectAllBins();
                                        }}
                                    >
                                        {t("common.all")}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="xs"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            clearAllBins();
                                        }}
                                    >
                                        {t("common.clear")}
                                    </Button>
                                </div>
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator/>
                            {(allBins?.length > 0 ? allBins : AVAILABLE_BINS).map(bin => (
                                <DropdownMenuCheckboxItem
                                    key={bin}
                                    checked={selectedBins?.includes(bin)}
                                    onSelect={(e) => e.preventDefault()} // Prevent selection which would close the dropdown
                                    onClick={(e) => {
                                        e.preventDefault(); // Prevent default click behavior
                                        handleBinChange(bin);
                                    }}
                                >
                                    {bin}
                                </DropdownMenuCheckboxItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>


                    {/* Time Period Filter */}
                    <DropdownMenu
                        open={timeRangeDropdownOpen}
                        onOpenChange={(open) => handleOpenChange(open, 'period')}
                    >
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm"
                            >
                                <Clock className="h-3 w-3 sm:h-4 sm:w-4 opacity-50 mr-1"/>
                                <span className="hidden sm:inline">{t('header.filters.period')}: </span>
                                {selectedPeriod ? getTimePeriodDisplay(selectedPeriod) : t("common.select")}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            className="w-40"
                            onCloseAutoFocus={(e) => {
                                // Prevent auto-focus on close, which can cause unwanted scrolling
                                e.preventDefault();
                            }}
                        >
                            <DropdownMenuLabel>{t("header.filters.selectTimePeriod")}</DropdownMenuLabel>
                            <DropdownMenuSeparator/>
                            {displayTimePeriods.map(period => (
                                <DropdownMenuCheckboxItem
                                    key={period}
                                    checked={selectedPeriod === period}
                                    onSelect={(e) => e.preventDefault()} // Prevent selection which would close the dropdown
                                    onClick={(e) => {
                                        e.preventDefault(); // Prevent default click behavior
                                        handlePeriodChange(period);
                                    }}
                                >
                                    {getTimePeriodDisplay(period)}
                                </DropdownMenuCheckboxItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        </header>
    );
}